import { For } from "solid-js";

/**
 * @typedef {object} UITabDef
 * @property {string} id - The tab's identifier
 * @property {string} label - The tab's visible text
 */

/**
 * A tab strip with roving focus, for a surface that shows one panel at a time.
 *
 * Panels are rendered by the caller and must carry `id={`${tab.id}-panel`}` and
 * `aria-labelledby={`${tab.id}-tab`}` for the wiring here to resolve.
 * @param {object} props Component props
 * @param {UITabDef[]} props.tabs The tabs to offer
 * @param {string} props.active The active tab id
 * @param {(id: string) => void} props.onSelect Called with the newly selected tab id
 * @returns {import('solid-js').JSX.Element} The tab strip
 */
export function UITabs(props) {
  const tabClass = (isActive) =>
    `tw:flex tw:items-center tw:gap-1 tw:border-b-2 tw:px-3 tw:py-1.5 tw:text-dense ${
      isActive
        ? "tw:border-primary tw:font-medium"
        : "tw:border-transparent tw:text-base-content/70 tw:hover:text-base-content"
    }`;

  const handleKeyDown = (event) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step) return;

    event.preventDefault();

    const ids = props.tabs.map((tab) => tab.id);
    const next = (ids.indexOf(props.active) + step + ids.length) % ids.length;

    props.onSelect(ids[next]);
    document.getElementById(`${ids[next]}-tab`)?.focus();
  };

  return (
    <div
      role="tablist"
      onKeyDown={handleKeyDown}
      className="tw:flex tw:gap-1 tw:overflow-x-auto tw:border-b tw:border-base-content/20"
    >
      <For each={props.tabs}>
        {(tab) => (
          <button
            type="button"
            role="tab"
            id={`${tab.id}-tab`}
            aria-selected={props.active === tab.id}
            aria-controls={`${tab.id}-panel`}
            tabIndex={props.active === tab.id ? 0 : -1}
            className={tabClass(props.active === tab.id)}
            onClick={() => props.onSelect(tab.id)}
          >
            {tab.label}
          </button>
        )}
      </For>
    </div>
  );
}
